import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, EASE_EDITORIAL } from "../components/Reveal";
import { PROJECTS, getProject } from "../data/projects";

const NotFound = () => (
  <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6 px-6 text-center">
    <h1 className="font-serif text-6xl text-ink">Frame not found</h1>
    <Link to="/" className="text-[0.7rem] uppercase tracking-[0.24em] text-oxblood hover:underline">
      ← Return to the archive
    </Link>
  </div>
);

const ProjectDetail = () => {
  const { slug } = useParams();
  const project = getProject(slug);

  const next = useMemo(() => {
    if (!project) return null;
    const idx = PROJECTS.findIndex((p) => p.slug === slug);
    return PROJECTS[(idx + 1) % PROJECTS.length];
  }, [project, slug]);

  if (!project) return <NotFound />;

  const isFilm = project.type === "film";

  return (
    <article data-testid="project-detail-page" className="bg-ivory">
      {/* Opening */}
      <header className="relative bg-noir text-noir-text">
        <motion.img
          src={project.cover}
          alt={project.title}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2, ease: EASE_EDITORIAL }}
          className="absolute inset-0 h-full w-full object-cover object-[center_25%] opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-noir/90 via-noir/30 to-noir/50" />

        <div className="relative mx-auto flex min-h-[88svh] max-w-[1600px] flex-col justify-end px-6 pb-16 pt-36 sm:px-10 sm:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 1.2, ease: EASE_EDITORIAL }}
          >
            <Link
              to="/"
              data-testid="back-to-archive-link"
              className="text-[0.7rem] uppercase tracking-[0.24em] text-gold hover:text-ivory"
            >
              ← The Archive
            </Link>
            <div className="mt-6 flex items-center gap-4">
              <span className="text-[0.7rem] uppercase tracking-[0.24em] text-ivory/60">
                {project.index}
              </span>
              <span className="h-px w-10 bg-gold/60" />
              <span className="text-[0.7rem] uppercase tracking-[0.24em] text-ivory/60">
                {project.medium} — {project.year}
              </span>
            </div>
            <h1 className="mt-6 max-w-5xl font-serif text-6xl font-light leading-[0.88] tracking-tight text-ivory sm:text-7xl lg:text-[8rem]">
              {project.title}
            </h1>
          </motion.div>
        </div>
      </header>

      {/* Concept statement */}
      <section className="border-b border-line px-6 py-24 sm:px-10 sm:py-32">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <Reveal>
              <span className="text-[0.7rem] uppercase tracking-[0.24em] text-gold">Concept</span>
            </Reveal>
          </div>
          <div className="lg:col-span-9">
            <Reveal delay={0.05}>
              <p className="font-serif text-3xl font-light leading-snug text-ink sm:text-4xl lg:text-5xl lg:leading-[1.1]">
                {project.concept}
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-10 max-w-2xl text-base leading-relaxed text-ink-soft">
                {project.statement}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Process + Credits */}
      <section className="border-b border-line px-6 py-20 sm:px-10 sm:py-28">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="text-[0.7rem] uppercase tracking-[0.24em] text-gold">
                Process Notes
              </span>
            </Reveal>
            <ul className="mt-8 space-y-6">
              {project.process.map((p, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <li className="flex gap-5 border-b border-line pb-6">
                    <span className="font-serif text-2xl italic text-gold">0{i + 1}</span>
                    <span className="text-base leading-relaxed text-ink-soft">{p}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal>
              <span className="text-[0.7rem] uppercase tracking-[0.24em] text-gold">Credits</span>
            </Reveal>
            <dl className="mt-8 space-y-5">
              {project.credits.map((c, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="flex flex-col gap-1 border-b border-line pb-4">
                    <dt className="text-[0.7rem] uppercase tracking-[0.2em] text-ink-soft">
                      {c.role}
                    </dt>
                    <dd className="font-serif text-xl text-ink">{c.name}</dd>
                    {c.note && (
                      <span className="mt-1 font-serif text-base italic text-ink-soft">
                        {c.note}
                      </span>
                    )}
                  </div>
                </Reveal>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Selected frames */}
      <section className="px-6 py-24 sm:px-10 sm:py-32" data-testid="selected-frames">
        <div className="mx-auto max-w-[1600px]">
          <Reveal>
            <h2 className="mb-16 font-serif text-4xl font-light italic text-ink sm:text-5xl">
              Selected Frames
            </h2>
          </Reveal>

          {isFilm ? (
            <Reveal>
              <div className="overflow-hidden border border-line bg-noir">
                <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                  <iframe
                    data-testid="film-embed"
                    src={`https://www.youtube.com/embed/${project.videoId}?rel=0&modestbranding=1`}
                    title={`${project.title} — film`}
                    className="absolute inset-0 h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
              <p className="mt-4 text-[0.7rem] uppercase tracking-[0.22em] text-ink-soft">
                After The Credits — experimental film, in full
              </p>
            </Reveal>
          ) : (
            <div className="columns-1 gap-6 sm:columns-2 lg:columns-3 [&>*]:mb-6">
              {project.gallery.map((src, i) => (
                <Reveal key={src} delay={(i % 3) * 0.06} className="break-inside-avoid">
                  <div className="overflow-hidden">
                    <img
                      src={src}
                      alt={`${project.title} — frame ${i + 1}`}
                      loading="lazy"
                      className="w-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.04]"
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Next project */}
      {next && (
        <Link
          to={`/work/${next.slug}`}
          data-testid="next-project-link"
          className="group block border-t border-line bg-surface px-6 py-20 sm:px-10 sm:py-28"
        >
          <div className="mx-auto flex max-w-[1600px] flex-col gap-3">
            <span className="text-[0.7rem] uppercase tracking-[0.24em] text-gold">
              Next — {next.index}
            </span>
            <h3 className="font-serif text-5xl font-light leading-none tracking-tight text-ink transition-colors duration-500 group-hover:text-oxblood sm:text-7xl">
              {next.title} <span className="italic">↗</span>
            </h3>
          </div>
        </Link>
      )}
    </article>
  );
};

export default ProjectDetail;
